import { View, Text, StyleSheet, ScrollView, Platform, TouchableOpacity, ActivityIndicator, TextInput } from 'react-native';
import { Stack, usePathname, useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesome6 } from '@expo/vector-icons';
import { useQueryClient } from '@tanstack/react-query';
import Header from '../../../components/Header';
import Footer from '../../../components/Footer';
import MetaTags from '../../../components/MetaTags';
import ProjectCard from '../../../components/ProjectCard';
import ForwardedDashboardCard from '../../../components/onboarding/ForwardedDashboardCard';
import OnboardingResumptionGate from '../../../components/onboarding/OnboardingResumptionGate';
import ProjectCreationUpgradeBanner from '../../../components/billing/ProjectCreationUpgradeBanner';
import LegacyAccessEndedBanner from '../../../components/billing/LegacyAccessEndedBanner';
import { useUserProjects } from '../../../lib/useUserProjects';
import { useFontLoading } from '../../../lib/FontContext';
import { interSemiBold, interRegular } from "../../../lib/fonts";
import ChatModal from '../../../components/discovery/Chat';
import colors from '../../../lib/colors';
import { createProjectFromChatDraft, type ChatDigestDraft } from '../../../lib/chatProjectCreation';
import { buildCanonicalDashboardPath, buildDashboardPath } from '../../../lib/dashboardRoutes';
import { buildForwardedOnboardingRoute } from '../../../lib/forwardedOnboarding';
import { useForwardedOnboardingState } from '../../../lib/hooks/useForwardedOnboardingState';
import { CURRENT_ENTITLEMENTS_QUERY_KEY } from '../../../lib/hooks/useCurrentEntitlements';
import { requiresSubscriptionForProjectCreation } from '../../../lib/projectCreationEntitlements';
import { useUserSettingDismissal } from '../../../lib/hooks/useUserSettingDismissal';
import { useProjectCreationGate } from '../../../lib/hooks/useProjectCreationGate';
import { getLegacyPausedProjectIds, isLegacyAccessEnded } from '../../../lib/legacyTransition';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useOptionalResolvedMessage } from '../../../lib/i18n/messages';
import { filterDashboardProjects } from '../../../lib/dashboardProjectSearch';

const DASHBOARD_PROJECT_LIMIT_BANNER_DISMISSED_KEY = 'ui.dashboard_project_limit_banner_dismissed';

export default function DashboardRoute() {
    const pathname = usePathname();

    return (
        <OnboardingResumptionGate fallback={buildDashboardPath(pathname)}>
            <DashboardScreen />
        </OnboardingResumptionGate>
    );
}

function DashboardScreen() {
    const { i18n } = useLingui();
    const { projects, isLoading, error, refetch } = useUserProjects();
    const resolvedError = useOptionalResolvedMessage(error);
    const router = useRouter();
    const pathname = usePathname();
    const { fontsLoaded } = useFontLoading();
    const queryClient = useQueryClient();
    const projectCreationGate = useProjectCreationGate();
    const [searchQuery, setSearchQuery] = useState('');
    const filteredProjects = useMemo(() => filterDashboardProjects(projects, searchQuery), [projects, searchQuery]);
    const [isChatVisible, setIsChatVisible] = useState(false);
    const projectLimitBannerDismissal = useUserSettingDismissal(DASHBOARD_PROJECT_LIMIT_BANNER_DISMISSED_KEY);
    const hasProjects = projects.length > 0;
    const hasMultipleProjects = projects.length > 1;
    const isProjectCreationBlocked = projectCreationGate.isBlocked;
    const needsPreviewTrial = requiresSubscriptionForProjectCreation(projectCreationGate.snapshot);
    const legacyAccessEnded = isLegacyAccessEnded(projectCreationGate.snapshot);
    const legacyPausedProjectIds = getLegacyPausedProjectIds(projectCreationGate.snapshot);
    const shouldCheckForwardedOnboarding = !projectCreationGate.isChecking
        && !isProjectCreationBlocked
        && !isLoading
        && !error
        && !hasProjects;
    const forwardedOnboardingState = useForwardedOnboardingState({
        areProjectsLoading: isLoading,
        nonInboxProjectCount: projects.length,
        projectsError: error,
        skip: !shouldCheckForwardedOnboarding,
    });
    const showForwardedDashboardCard = !isLoading
        && !error
        && !projectCreationGate.isChecking
        && !isProjectCreationBlocked
        && !forwardedOnboardingState.isLoading
        && !hasProjects
        && forwardedOnboardingState.isEligible;
    const showForwardedLoadingCard = !isLoading
        && !error
        && !projectCreationGate.isChecking
        && !isProjectCreationBlocked
        && !hasProjects
        && forwardedOnboardingState.isLoading;

    const handleChatComplete = async (data: ChatDigestDraft, context?: { chatSessionId: string | null }) => {
        console.log("Chat completed with data:", data);

        const result = await createProjectFromChatDraft(data, {
            source: 'chat',
            chatSessionId: context?.chatSessionId ?? null,
        });
        void queryClient.invalidateQueries({ queryKey: CURRENT_ENTITLEMENTS_QUERY_KEY });
        router.replace(result.destination);
    };

    useEffect(() => {
        if (!showForwardedDashboardCard) {
            return;
        }

        router.replace(buildForwardedOnboardingRoute(pathname));
    }, [pathname, router, showForwardedDashboardCard]);

    return (
        <>
            <MetaTags
                title="Dashboard - Paperzilla"
                description="Manage your research projects on Paperzilla"
                url={`https://paperzilla.ai${buildCanonicalDashboardPath()}`}
            />
            <Stack.Screen options={{ headerShown: false }} />
            <View style={styles.page}>
                <Header />
                <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
                    <View style={styles.contentWrapper}>
                    <View style={styles.content}>
                        <View style={styles.titleRow}>
                            <Text
                                style={[styles.title, fontsLoaded && interSemiBold]}
                                accessibilityRole="header"
                                {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 1 })}
                            ><Trans id="digests.page.projects" comment="UI copy in app/(app)/digests/index.tsx">
                                Projects
                            </Trans></Text>

                            <TouchableOpacity
                                style={styles.mcpButton}
                                onPress={() => router.push('/ai-connections?from=dashboard')}
                                activeOpacity={0.8}
                            >
                                <FontAwesome6 name="plug" size={13} color={colors.primaryGreen} />
                                <Text style={[styles.mcpButtonText, fontsLoaded && interSemiBold]}><Trans id="digests.page.connectAiClients" comment="UI copy in app/(app)/digests/index.tsx">
                                    Connect AI clients
                                </Trans></Text>
                            </TouchableOpacity>
                        </View>

                        <Text style={[styles.subtitle, fontsLoaded && interRegular]}><Trans id="digests.page.eachProjectIncludesALiveFeedAppRssAnd" comment="UI copy in app/(app)/digests/index.tsx">
                            Each project includes a live Feed (app + RSS) and Digest emails.
                        </Trans></Text>

                        <LegacyAccessEndedBanner
                            snapshot={projectCreationGate.snapshot}
                            projects={projects}
                        />

                        {/* Search Bar */}
                        {hasMultipleProjects && (
                            <View style={styles.searchContainer}>
                                <FontAwesome6 name="magnifying-glass" size={16} color={colors.text500} style={styles.searchIcon} />
                                <TextInput
                                    style={[styles.searchInput, fontsLoaded && interRegular]}
                                    placeholder={i18n._(msg({ id: 'dashboard.searchProjects', message: 'Search your projects' }))}
                                    placeholderTextColor={colors.text500}
                                    value={searchQuery}
                                    onChangeText={setSearchQuery}
                                />
                            </View>
                        )}

                        {showForwardedDashboardCard ? (
                            <ForwardedDashboardCard
                                importCount={forwardedOnboardingState.importCount}
                                importedPaperCount={forwardedOnboardingState.importedPaperCount}
                                onSetUpTopic={() => projectCreationGate.guardProjectCreation(() => {
                                    router.push(buildForwardedOnboardingRoute(pathname));
                                })}
                                onOpenInbox={() => router.push(buildDashboardPath(pathname, '/inbox'))}
                            />
                        ) : showForwardedLoadingCard ? (
                            <View style={styles.buildCard}>
                                <ActivityIndicator size="small" color={colors.primaryGreen} />
                                <Text style={[styles.buildTitle, fontsLoaded && interSemiBold]}><Trans id="digests.page.checkingYourInbox" comment="UI copy in app/(app)/digests/index.tsx">
                                    Checking your Inbox
                                </Trans></Text>
                                <Text style={[styles.buildSubtitle, fontsLoaded && interRegular]}><Trans id="digests.page.lookingForForwardedImportsBeforeShowingYourEmptyDashboardState" comment="UI copy in app/(app)/digests/index.tsx">
                                    Looking for forwarded imports before showing your empty dashboard state.
                                </Trans></Text>
                            </View>
                        ) : projectCreationGate.isChecking ? (
                            <View style={styles.buildCard}>
                                <ActivityIndicator size="small" color={colors.primaryGreen} />
                                <Text style={[styles.buildTitle, fontsLoaded && interSemiBold]}><Trans id="digests.page.checkingProjectAccess" comment="UI copy in app/(app)/digests/index.tsx">
                                    Checking project access
                                </Trans></Text>
                                <Text style={[styles.buildSubtitle, fontsLoaded && interRegular]}><Trans id="digests.page.confirmingWhetherThisAccountCanCreateAnotherTopic" comment="UI copy in app/(app)/digests/index.tsx">
                                    Confirming whether this account can create another topic.
                                </Trans></Text>
                            </View>
                        ) : isProjectCreationBlocked ? (
                            needsPreviewTrial || projectCreationGate.isUnavailable ? (
                                <ProjectCreationUpgradeBanner
                                    dismissable={false}
                                    style={styles.projectLimitBanner}
                                    snapshot={projectCreationGate.snapshot}
                                />
                            ) : legacyAccessEnded ? null : (
                                projectLimitBannerDismissal.isDismissed === false && (
                                    <ProjectCreationUpgradeBanner
                                        onDismiss={projectLimitBannerDismissal.dismiss}
                                        style={styles.projectLimitBanner}
                                        snapshot={projectCreationGate.snapshot}
                                    />
                                )
                            )
                        ) : (
                            <TouchableOpacity
                                style={styles.buildCard}
                                onPress={() => projectCreationGate.guardProjectCreation(() => setIsChatVisible(true))}
                                activeOpacity={0.7}
                            >
                                <View style={styles.buildIconContainer}>
                                    <FontAwesome6
                                        name="plus"
                                        size={32}
                                        color={colors.primaryGreen}
                                    />
                                </View>
                                <Text style={[styles.buildTitle, fontsLoaded && interSemiBold]}>
                                    {hasProjects ? <Trans id="digests.page.createProject" comment="Conditional UI copy in app/(app)/digests/index.tsx">Create project</Trans> : <Trans id="digests.page.createYourFirstProject" comment="Conditional UI copy in app/(app)/digests/index.tsx">Create my first project</Trans>}
                                </Text>
                                <Text style={[styles.buildSubtitle, fontsLoaded && interRegular]}>
                                    {hasProjects
                                        ? <Trans id="digests.page.trackAnotherTopicWithFeedUpdatesAndDigestEmails" comment="Conditional UI copy in app/(app)/digests/index.tsx">Track another topic with feed updates and digest emails</Trans>
                                        : <Trans id="digests.page.trackOneResearchTopicFollowItInALiveFeed" comment="Conditional UI copy in app/(app)/digests/index.tsx">Track one research topic, follow it in a live feed, and get digest emails.</Trans>}
                                </Text>
                            </TouchableOpacity>
                        )}

                        <View style={styles.section}>
                            {/* Loading state */}
                            {isLoading && (
                                <View style={styles.loadingCard}>
                                    <ActivityIndicator size="small" color={colors.primaryGreen} />
                                    <Text style={styles.loadingText}><Trans id="digests.page.loadingYourProjects" comment="UI copy in app/(app)/digests/index.tsx">Loading your projects...</Trans></Text>
                                </View>
                            )}

                            {/* Error state */}
                            {!isLoading && error && (
                                <View style={styles.errorCard}>
                                    <Text style={styles.errorTitle}><Trans id="digests.page.failedToLoadProjects" comment="UI copy in app/(app)/digests/index.tsx">⚠️ Failed to Load Projects</Trans></Text>
                                    <Text style={styles.errorText}>{resolvedError}</Text>
                                    <TouchableOpacity style={styles.retryButton} onPress={refetch}>
                                        <Text style={styles.retryButtonText}><Trans id="digests.page.tryAgain" comment="UI copy in app/(app)/digests/index.tsx">Try Again</Trans></Text>
                                    </TouchableOpacity>
                                </View>
                            )}

                            {/* Project cards */}
                            {!isLoading && !error && hasProjects && (
                                <View style={styles.projectsGrid}>
                                    {filteredProjects.map((project) => (
                                        <ProjectCard
                                            key={project.id}
                                            project={project}
                                            legacyTransitionPaused={legacyPausedProjectIds.has(project.id)}
                                        />
                                    ))}
                                </View>
                            )}
                            {!isLoading && !error && hasProjects && filteredProjects.length === 0 && (
                                <View style={styles.loadingCard} testID="dashboard-search-empty">
                                    <Text style={styles.loadingText}><Trans id="dashboard.search.noMatches" comment="Empty dashboard project-search result">No matching projects</Trans></Text>
                                    <TouchableOpacity style={styles.retryButton} onPress={() => setSearchQuery('')} accessibilityRole="button">
                                        <Text style={styles.retryButtonText}><Trans id="dashboard.search.clear" comment="Reset the dashboard project search to show all existing projects">Clear search</Trans></Text>
                                    </TouchableOpacity>
                                </View>
                            )}
                        </View>
                    </View>
                    </View>
                    <Footer />
                </ScrollView>
                <StatusBar style="auto" />
            </View>

            <ChatModal
                visible={isChatVisible}
                onClose={() => setIsChatVisible(false)}
                onComplete={handleChatComplete}
                entrySurface="dashboard"
            />
        </>
    );
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
        paddingHorizontal: 0,
        paddingBottom: 0,
    },
    contentWrapper: {
        flex: 1,
    },
    content: {
        width: '100%',
        maxWidth: 1200,
        alignSelf: 'center',
        paddingHorizontal: 20,
        paddingTop: 20,
        paddingBottom: 40,
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 8,
    },
    title: {
        flexShrink: 1,
        fontSize: 36,
        fontWeight: '600',
        color: colors.primaryGreen,
        marginBottom: 0,
    },
    subtitle: {
        fontSize: 16,
        color: colors.text700,
        marginBottom: 24,
        lineHeight: 24,
    },
    mcpButton: {
        marginLeft: 'auto',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: colors.primaryGreen,
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 9,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    mcpButtonText: {
        fontSize: 14,
        color: colors.primaryGreen,
        fontWeight: '600',
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.card,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.border,
        paddingHorizontal: 12,
        paddingVertical: 10,
        marginBottom: 24,
    },
    searchIcon: {
        marginRight: 8,
    },
    searchInput: {
        flex: 1,
        fontSize: 14,
        color: colors.text900,
        ...(Platform.OS === 'web' && {
            outlineStyle: 'none' as any,
        }),
    },
    buildCard: {
        backgroundColor: colors.card,
        padding: 40,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.border,
        alignItems: 'center',
        marginBottom: 24,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.2s',
            ':hover': {
                borderColor: colors.primaryGreen,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            },
        }),
    },
    buildIconContainer: {
        width: 64,
        height: 64,
        borderRadius: 32,
        borderWidth: 2,
        borderColor: colors.primaryGreen,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    buildTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: colors.primaryGreen,
        marginBottom: 4,
    },
    buildSubtitle: {
        fontSize: 14,
        color: colors.text700,
    },
    projectLimitBanner: {
        marginTop: 0,
        marginBottom: 24,
    },
    section: {
        marginBottom: 32,
    },
    loadingCard: {
        backgroundColor: '#f0f9ff',
        padding: 24,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.primaryGreen,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
    },
    loadingText: {
        fontSize: 16,
        color: colors.primaryGreenDark,
        fontWeight: '500',
    },
    errorCard: {
        backgroundColor: '#fee2e2',
        padding: 24,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.errorRed,
        alignItems: 'center',
    },
    errorTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: colors.errorRed,
        marginBottom: 8,
    },
    errorText: {
        fontSize: 14,
        color: '#991b1b',
        textAlign: 'center',
        marginBottom: 16,
        lineHeight: 20,
    },
    retryButton: {
        backgroundColor: colors.errorRed,
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 8,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    retryButtonText: {
        color: '#fff',
        fontSize: 14,
        fontWeight: '600',
    },
    projectsGrid: {
        gap: 12,
    },
});
